import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getProfile, updateProfile } from '@/lib/profile/api';
import { useDensity, type DensityPreference } from '@/theme/use-density';
import type { Language, OperatorProfile } from '@/lib/profile/types';
import { MyAccess } from './my-access';

// /profile route per docs/ui/operator-ui-tds.md §4.1 (NEX-170).
//
// Two tabs (`?tab=`): "Preferencias" — per-operator prefs (language / density /
// notification toggles) — and "Mi acceso" (users-roles-permissions §5 P7: my roles, scope
// and effective permissions). No permission gate — every signed-in member has a profile.

// English is the only language available for now (see @/i18n) — the es / pt-BR
// options return here when the console re-enables them.
// Both tables hold KEYS, never language — the component renders them with `t()`.
const LANGUAGES: { value: Language; labelKey: string }[] = [
  { value: 'en', labelKey: 'profile.languages.en' },
];

const DENSITIES: { value: DensityPreference; labelKey: string }[] = [
  { value: 'auto', labelKey: 'common:density.auto' },
  { value: 'comfortable', labelKey: 'common:density.comfortable' },
  { value: 'compact', labelKey: 'common:density.compact' },
];

const TABS = ['preferences', 'access'] as const;
type ProfileTab = (typeof TABS)[number];

export function ProfileRoute() {
  const { t } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: ProfileTab = TABS.includes(raw as ProfileTab) ? (raw as ProfileTab) : 'preferences';
  const setTab = (next: string): void => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  return (
    <main className="density-page flex h-full min-h-0 flex-col overflow-hidden">
      <header className="px-4 pt-4 lg:px-6 lg:pt-6">
        <h1 className="text-2xl font-semibold">{t('profile.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('profile.subtitle')}</p>
      </header>
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
        <div className="overflow-x-auto px-4 pt-3 lg:px-6">
          <TabsList>
            <TabsTrigger value="preferences">{t('profile.tabs.preferences')}</TabsTrigger>
            <TabsTrigger value="access">{t('profile.tabs.access')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="preferences" className="mt-0 min-h-0 flex-1 overflow-y-auto p-4 lg:px-6">
          <Preferences />
        </TabsContent>
        <TabsContent value="access" className="mt-0 min-h-0 flex-1 overflow-y-auto p-4 lg:px-6">
          <div className="max-w-4xl">
            <MyAccess />
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}

function Preferences() {
  const { t } = useTranslation('admin');
  const density = useDensity();
  const profile = useQuery({
    queryKey: ['profile'] as const,
    queryFn: getProfile,
    staleTime: 5 * 60_000,
  });
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (input: Partial<OperatorProfile>) => updateProfile(input),
    onSuccess: (updated) => {
      qc.setQueryData(['profile'], updated);
    },
  });

  const [draft, setDraft] = useState<OperatorProfile | null>(null);
  useEffect(() => {
    if (profile.data && !draft) setDraft(profile.data);
  }, [profile.data, draft]);

  if (profile.isLoading || !draft) {
    return (
      <div className="max-w-2xl">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const patch = (partial: Partial<OperatorProfile>) => setDraft({ ...draft, ...partial });
  const patchNotifications = (partial: Partial<OperatorProfile['notifications']>) =>
    setDraft({ ...draft, notifications: { ...draft.notifications, ...partial } });

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4 p-6">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground/80">{t('profile.fields.language')}</span>
            <select
              value={draft.language}
              onChange={(e) => patch({ language: e.target.value as Language })}
              className="w-48 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value}>
                  {t(l.labelKey)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground/80">{t('profile.fields.density')}</span>
            <select
              value={density.preference}
              onChange={(e) => density.setDensity(e.target.value as DensityPreference)}
              className="w-48 rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              {DENSITIES.map((d) => (
                <option key={d.value} value={d.value}>
                  {t(d.labelKey)}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">{t('common:density.deviceHint')}</span>
          </label>

          <fieldset className="rounded-md border border-input p-3">
            <legend className="px-2 text-sm font-medium">
              {t('profile.notifications.legend')}
            </legend>
            <div className="mt-2 flex flex-col gap-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.notifications.dashboardAlerts}
                  onChange={(e) => patchNotifications({ dashboardAlerts: e.target.checked })}
                />
                {t('profile.notifications.dashboardAlerts')}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.notifications.caseAssignments}
                  onChange={(e) => patchNotifications({ caseAssignments: e.target.checked })}
                />
                {t('profile.notifications.caseAssignments')}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.notifications.digestEmail}
                  onChange={(e) => patchNotifications({ digestEmail: e.target.checked })}
                />
                {t('profile.notifications.digestEmail')}
              </label>
            </div>
          </fieldset>

          <div className="flex justify-end">
            <Button
              onClick={() =>
                save.mutate({ language: draft.language, notifications: draft.notifications })
              }
              disabled={save.isPending}
            >
              {save.isPending ? t('profile.saving') : t('profile.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
